import { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Lock, Loader2, MailCheck } from "lucide-react";
import { Input } from "@/components/ui/input";
import { API } from "@/lib/config";
import { formatApiErrorDetail } from "@/lib/errors";

const GoogleIcon = () => (
  <svg viewBox="0 0 48 48" className="w-5 h-5" aria-hidden="true"><path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3C33.7 32.7 29.2 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.8 1.2 7.9 3.1l5.7-5.7C34 6.1 29.3 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.1-2.4-.4-3.5z"/><path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 15.1 19 12 24 12c3.1 0 5.8 1.2 7.9 3.1l5.7-5.7C34 6.1 29.3 4 24 4 16.3 4 9.7 8.3 6.3 14.7z"/><path fill="#4CAF50" d="M24 44c5.2 0 9.9-2 13.4-5.2l-6.2-5.2C29.2 35.1 26.7 36 24 36c-5.2 0-9.6-3.3-11.3-8l-6.5 5C9.5 39.6 16.2 44 24 44z"/><path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.2-2.2 4.2-4.1 5.6l6.2 5.2C37 39.2 44 34 44 24c0-1.3-.1-2.4-.4-3.5z"/></svg>
);

export const AdminLogin = ({ onLogin, authError }) => {
  const [email, setEmail] = useState("");
  const [p, setP] = useState("");
  const [err, setErr] = useState(authError || "");
  const [loading, setLoading] = useState(false);
  const [forgot, setForgot] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true); setErr("");
    try {
      if (forgot) {
        await axios.post(`${API}/auth/forgot-password`, { email });
        setSent(true);
      } else {
        const { data } = await axios.post(`${API}/auth/login`, { email, password: p });
        onLogin(data);
      }
    } catch (e2) { setErr(formatApiErrorDetail(e2.response?.data?.detail)); } finally { setLoading(false); }
  };

  const google = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/gestione";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen grid place-items-center bg-slate-50 water-bg px-4 py-8" data-testid="admin-login-page">
      <form onSubmit={submit} className="w-full max-w-sm rounded-2xl bg-white border border-slate-200 shadow-sm p-6 sm:p-8 space-y-4" data-testid="admin-login-form">
        <span className="w-12 h-12 rounded-xl bg-sky-600 text-white grid place-items-center"><Lock className="w-6 h-6" /></span>
        <div>
          <h1 className="text-2xl font-extrabold tracking-tight">{forgot ? "Password dimenticata" : "Area gestione"}</h1>
          <p className="text-sm text-slate-500 mt-1">{forgot ? "Inserisci la tua email: ti mandiamo un link per impostare una nuova password." : "Accedi con email e password oppure con Google."}</p>
        </div>
        {sent ? (
          <p className="flex gap-2 text-sm text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-xl p-3" data-testid="admin-forgot-sent"><MailCheck className="w-5 h-5 shrink-0" /> Se l'email è autorizzata, riceverai a breve il link (valido 1 ora).</p>
        ) : (
          <>
            <Input type="email" required autoComplete="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Email" className="h-12" data-testid="admin-email-input" />
            {!forgot && <Input type="password" required autoComplete="current-password" value={p} onChange={(e) => setP(e.target.value)} placeholder="Password" className="h-12" data-testid="admin-password-input" />}
            {err && <p className="text-sm font-semibold text-red-600" data-testid="admin-login-error">{err}</p>}
            <button disabled={loading} className="w-full h-12 rounded-full bg-sky-600 text-white font-bold hover:bg-sky-700 disabled:opacity-60 inline-flex items-center justify-center gap-2 transition-colors" data-testid="admin-login-submit">
              {loading && <Loader2 className="w-4 h-4 animate-spin" />} {forgot ? "Invia link" : "Entra"}
            </button>
          </>
        )}
        <button type="button" onClick={() => { setForgot(!forgot); setSent(false); setErr(""); }} className="block w-full text-center text-sm font-semibold text-sky-700 py-1" data-testid="admin-forgot-toggle">
          {forgot ? "Torna all'accesso" : "Password dimenticata?"}
        </button>
        {!forgot && (
          <>
            <div className="flex items-center gap-3 text-xs text-slate-400"><span className="flex-1 h-px bg-slate-200" />oppure<span className="flex-1 h-px bg-slate-200" /></div>
            <button type="button" onClick={google} className="w-full h-12 rounded-full border border-slate-300 bg-white font-bold hover:bg-slate-50 inline-flex items-center justify-center gap-3 transition-colors" data-testid="admin-google-login-btn">
              <GoogleIcon /> Accedi con Google
            </button>
          </>
        )}
        <Link to="/" className="block text-center text-sm text-slate-500 hover:text-sky-700 py-1" data-testid="admin-login-back-link">Torna al sito</Link>
      </form>
    </div>
  );
};
